import UpfrontCollection from "@components/UpfrontCollection";
import { validateEpicPassOnLeg } from "@lib/services/add_epic_pass";
import Session from "@lib/session";
import useConfig from "@lib/use_config";
import { getContent } from "@lib/use_content";
import { formatISO, parseISO } from "date-fns";
import { usePathname, useRouter } from "next/navigation";
import React, { useState } from "react";

type LocationPredictionRendererProps = {
  final: string;
  key: string;
  title: string;
  description?: string;
  source: string;
  type: string;
  typestring: string;
};

interface UCProps {
  configLoading?: boolean;
  disableFields?: boolean;
  findLocations?: be.FindLocationsFunction;
  limitLocations?: number;
  locationPredictionRenderer?: React.FunctionComponent<LocationPredictionRendererProps>;
  locationPredictionSelected?: be.LocationPredictionSelectedFunction;
  noMap?: boolean;
  onReloadService?: () => void;
  onFormReady?: (complete: boolean) => void;
  onSubmit?: () => void;
  servicesShown: boolean;
  servicesUrl?: string;
}

function UpfrontContainer({
  configLoading,
  disableFields = false,
  findLocations,
  limitLocations,
  locationPredictionRenderer,
  locationPredictionSelected,
  noMap = false,
  onFormReady,
  onReloadService,
  onSubmit,
  servicesShown,
  servicesUrl,
}: UCProps): JSX.Element | null {
  const {
    loading: cfgLoading,
    data: cfgData,
    error: cfgError,
  } = useConfig(["openReservationMode", "customerRequestMode"]);
  const router = useRouter();
  const pathname = usePathname();

  const [submitting, setSubmitting] = useState(false);

  const submit = async function (data: be.LandingSubmitData) {
    setSubmitting(true);
    // Anything thrown past this point would otherwise leave the continue button
    // spinning with no way to retry. On the happy path a widget hand-off or a
    // route change deliberately keeps the spinner up — the page is going away.
    try {
      const openResCollectDate = cfgData?.config.openReservationMode === "enabled_collect_date";
      const dateOnlyMode = cfgData?.config.customerRequestMode === 2;
      const customerRequest = openResCollectDate || dateOnlyMode ? true : data.customerRequest;
      const isOpenRes =
        Session.getCurrentLeg()?.open || cfgData?.config.openReservationMode == "enabled";
      const dateIsFlight =
        !customerRequest && [data.pickup.typeString, data.dropoff.typeString].includes("airport");

      const newLeg: be.Reservation = {
        ...leg,
        customerRequest,
        dropoff: data.dropoff,
        pickup: data.pickup,
        service: {},
      };

      if (isOpenRes) newLeg.open = true;

      if (!isOpenRes || cfgData?.config.openReservationMode == "enabled_collect_date" || dateOnlyMode) {
        newLeg.requestedPickupTime =
          dateIsFlight || !data.datetime ? undefined : formatISO(data.datetime);
        newLeg.flight = {
          ...newLeg.flight,
          flightTime: dateIsFlight ? formatISO(data.datetime) : undefined,
        };
      }

      if (customerRequest && newLeg.flight) {
        delete newLeg.flight.flightTime;
      }

      Session.setCurrentLeg(newLeg);
      await validateEpicPassOnLeg(Session.getCurrentLegIndex());

      // Determine service area from selected locations (or default to config service area)
      let serviceArea: number | undefined;
      if (typeof data.pickup.serviceArea === "number") serviceArea = data.pickup.serviceArea;
      else if (typeof data.dropoff.serviceArea === "number") serviceArea = data.dropoff.serviceArea;
      else if (
        typeof data.pickup.serviceArea === "object" &&
        typeof data.dropoff.serviceArea === "object"
      ) {
        // Both pickup and dropoff have service area lists. Pick one of the overlapping areas.
        let intersection = data.pickup.serviceArea.filter((x: number[]) =>
          data.dropoff.serviceArea.includes(x),
        );
        serviceArea = intersection.length > 0 ? intersection[0] : Session.getConfig().serviceArea;
      } else serviceArea = Session.getConfig().serviceArea;

      // Get service area name (for analytics). getContent REJECTS on a load failure
      // (Apollo's default errorPolicy "none") and this label is non-critical, so a
      // content-service blip — or an Apollo store reset racing this click — must
      // never abort the booking-advance submit.
      let serviceAreaName: string | undefined;
      if (serviceArea) {
        try {
          const { data: content } = await getContent(["serviceAreaName"], serviceArea);
          serviceAreaName = content?.content?.serviceAreaName ?? undefined;
        } catch (e) {
          console.error("Failed to load service-area name content:", e);
        }
      }

      Session.updateBooking({ serviceArea, serviceAreaName });

      if (onSubmit) {
        onSubmit();
      } else if (!Session.isSecondLeg() && !String(pathname).match("/book/services")) {
        router.replace(servicesUrl || `/${window.siteId}/book/services`);
      } else if (Session.isSecondLeg() && !String(pathname).match("/book/return_services")) {
        router.replace(servicesUrl || `/${window.siteId}/book/return_services`);
      } else if (onReloadService) {
        onReloadService();
        setSubmitting(false);
      }
    } catch (e) {
      console.error("Landing submit failed:", e);
      setSubmitting(false);
    }
  };

  const leg = Session.getCurrentLeg();

  const upfrontValues: be.LandingData = {
    customerRequest: leg?.customerRequest || false,
    pickup: leg?.pickup,
    dropoff: leg?.dropoff,
    pudPerson: Session.getBooking().pudPersonId,
  };
  let datetime: string | undefined = leg?.requestedPickupTime;
  if ([leg?.pickup?.typeString, leg?.dropoff?.typeString].includes("airport") && !leg?.customerRequest)
    datetime = leg?.flight?.flightTime;
  if (datetime) upfrontValues.datetime = parseISO(datetime);

  return (
    <>
      <UpfrontCollection
        configLoading={configLoading ?? cfgLoading}
        fieldNames={{
          customerRequest: "customerRequest",
          pickup: "pickup",
          dropoff: "dropoff",
          datetime: "datetime",
        }}
        findLocations={findLocations}
        hideSubmit={servicesShown}
        limitLocations={limitLocations}
        locationPredictionRenderer={locationPredictionRenderer}
        locationPredictionSelected={locationPredictionSelected}
        noMap={noMap}
        onFormCompleteChange={function (complete: boolean) {
          if (onFormReady) onFormReady(complete);
        }}
        onSubmit={submit}
        submitting={submitting || disableFields}
        values={upfrontValues}
      />
    </>
  );
}

export default UpfrontContainer;
